import { css, cx } from '@linaria/core'
import { type Dispatch, type FC, type SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { EnhancedManCodeId } from '#api/enhancements/enhancedTmsApiTypes'
import { useAppNavigation } from '#app/navigation/useAppNavigation'
import { useAppDispatch, useAppSelector } from '#app/store'
import { addPendingConfiguration, createLocalConfigurationId } from '#app/store/slices/configurationSlice'
import { removeManCode } from '#app/store/slices/userDataSlice'
import { Button } from '#components/Button'
import { Dropdown } from '#components/Dropdown'
import { HoverTooltip } from '#components/HoverTooltip'
import { Icon } from '#components/Icon'
import { IconButton } from '#components/IconButton'
import { Input } from '#components/Input'
import { Select } from '#components/Select'
import type { ConfigurationMode } from '#features/Configuration/ApiConfiguration'
import { ManCodeTable } from '#features/ManCode/ManCodeTable'
import { useShowManCodeInformationOverlay } from '#features/ManCode/useShowManCodeInformationOverlay'
import ChevronRightIcon from '#icons/chevron-1-right-24.svg'
import CloseIcon from '#icons/close-48.svg'
import DeleteIcon from '#icons/delete-16.svg'
import DetailsIcon from '#icons/info-circle-outline-48.svg'
import PlusIcon from '#icons/plus-24.svg'
import { makeConfigurationLocation } from '#pages/Configuration/components/ConfigurationNavigation'
import { useCanOpenMoreConfigurations } from '#pages/Configuration/hooks/useCanOpenMoreConfigurations'
import { fragments, style } from '#styles/index'
import { useTranslatedManCodeOptionEntries } from '#utils/useTranslatedManCodeOptionEntries'
import { validateManCodeId } from '#utils/validateManCodeId'

const headerClass = css`
  cursor: pointer;
  padding: 0 12px 3.5px 12px;
  display: flex;
  align-items: center;
  align-self: flex-end;
  transition: all 250ms;

  &:hover {
    color: ${style.themeColors.textImportant};
    transition: all 250ms;
  }
`

const codeFormClass = css`
  display: flex;
  align-items: flex-start;
  justify-content: stretch;

  gap: 4px;

  padding: 0 16px 16px 16px;
  border-bottom: 1px solid ${style.themeColors.lineDefault};
  min-width: 500px;


  > ._input-group {
    flex: 1.3;
    position: relative;
    display: flex;
    align-items: center;
    max-width: 250px;
    
    > div {
      width: 100%;
    }
    
    input {
      height: 40px;
    }

    > ._clear {
      position: absolute;
      right: 12px;
      top: 0;
      height: 40px;
      opacity: 0;
      pointer-events: none;
      transition: 0.25s ease opacity;
      will-change: opacity;

      &._visible {
        pointer-events: unset;
        opacity: 1;
      }
    }
  }

  > ._details {
    height: 40px;
  }
  
  > ._select {
    flex: 1;
    > .select {
      height: 40px;

      > .value {
        ${fragments.textStyle.body.m.condensedBold}
      }
    }

    > .select-box {
      height: 40px;
      border: 2px solid ${style.themeColors.lineInput};

      > .entry {
        display: flex;
        gap: 4px;
        align-items: center;
      } 
    }
  }

  > ._button {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
  }
`

export interface ManCodeFieldProps {
  manCode: string | undefined
  setManCode: Dispatch<SetStateAction<string | undefined>>
}

export const ManCodeField: FC<ManCodeFieldProps> = (props) => {
  const [innerManCode, setInnerManCode] = useState<string | undefined>(undefined)
  const [mode, setMode] = useState<ConfigurationMode>('read-only')
  const { manCode, setManCode } = props.manCode ? props : { manCode: innerManCode, setManCode: setInnerManCode }

  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const id = useMemo(() => createLocalConfigurationId(), [])
  const concreteConfiguration = useAppSelector((x) => x.truckConfigurations.configurations[id])
  const { navigate } = useAppNavigation()
  const showManCode = useShowManCodeInformationOverlay()

  const loadManCode = useCallback(async () => {
    dispatch(
      addPendingConfiguration({
        id,
        callingApiConfiguration: {
          // do NOT apply the stored api configuration with a manCode
          manCode: manCode as EnhancedManCodeId,
          mode: mode,
        },
      }),
    )
  }, [dispatch, id, manCode, mode])

  useEffect(() => {
    if (concreteConfiguration?.creation.loadingStep === 'done') {
      navigate(makeConfigurationLocation(concreteConfiguration))
    }
  }, [concreteConfiguration, navigate])

  const options = useTranslatedManCodeOptionEntries()

  const validationError = validateManCodeId(manCode, t)
  const enabled = !!manCode && !validationError

  return (
    <div className={codeFormClass}>
      <div className="_input-group">
        <Input
          kind="text"
          placeholder={t('setup.code.input-placeholder', 'MAN-Code (TMS-…)')}
          error={validationError}
          value={manCode ?? ''}
          onChange={(evt) => setManCode(evt.currentTarget.value)}
          className="_input"
          prepareErrorMargins={true}
          prepareLabelMargins={false}
        />
        <IconButton
          className={cx('_clear', !!manCode && '_visible')}
          iconUrl={CloseIcon}
          title={''}
          onClick={(e) => {
            setManCode(undefined)
            ;(e.currentTarget as HTMLElement).blur()
          }}
        />
      </div>
      <IconButton
        className="_details"
        iconSize="l"
        iconUrl={DetailsIcon}
        title={t('code-table.actions.show-code-details', 'Show Code Details')}
        disabled={!enabled}
        onClick={(evt) => {
          if (!manCode) {
            return
          }
          evt.preventDefault()
          evt.stopPropagation()
          showManCode(manCode as EnhancedManCodeId)
        }}
      />
      <Select
        className="_select"
        // styleClassName="style-plain-button"
        options={options}
        value={mode}
        onChange={(item) => {
          item && setMode(item)
        }}
        slim
      />
      <Button onClick={loadManCode} disabled={!enabled} className="_button" kind="primary">
        {t('shared:common.button.load')} <Icon iconUrl={ChevronRightIcon} className="_icon" />
      </Button>
    </div>
  )
}

const manCodeHeaderDropdownClass = css`
  > h3 {
    display: flex;
    padding: 16px 16px 8px 16px;
    align-items: center;
    text-transform: uppercase;
    ${fragments.textStyle.heading.s.subtle};
    color: ${style.themeColors.textDeselected};
  }
`

const manCodeTableClass = css`
  padding: 0 16px 0 16px;
  max-height: 50vh;
  overflow: auto;
  scrollbar-gutter: stable;
`
const deleteIconClass = css`
  padding: 0 12px;
`
const DropdownContent: FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const recentManCodes = useAppSelector((x) => x.userData.recentManCodes)
  const [manCode, setManCode] = useState<string | undefined>()

  return (
    <div className={manCodeHeaderDropdownClass}>
      <h3>{t('setup.header.load-man-code', 'Load MAN-Code')}</h3>
      <ManCodeField manCode={manCode} setManCode={setManCode} />

      <ManCodeTable
        entries={recentManCodes.manCodes}
        className={manCodeTableClass}
        onManCodeSelected={({ manCode }) => {
          setManCode(manCode)
        }}
        InfoSlot={({ code }) => (
          <IconButton
            className={deleteIconClass}
            iconUrl={DeleteIcon}
            title={t('shared:common.button.delete')}
            onClick={(evt) => {
              // prevent bubbling, otherwise the MAN Code is being set, while deleting
              evt.preventDefault()
              if (code.manCode) {
                dispatch(removeManCode(code.manCode))
              }
            }}
          />
        )}
      />
    </div>
  )
}

const popupClass = css`
  padding: 0 0 16px 0;
`

export const ManCodeHeader: FC = () => {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()

  const canOpenMoreConfigurations = useCanOpenMoreConfigurations()

  return (
    <HoverTooltip.div
      className={headerClass}
      title={
        canOpenMoreConfigurations
          ? t('setup.header.create-from-man-code', 'Create from MAN-Code')
          : t('configuration.messages.please-close-configurations')
      }
      onClick={canOpenMoreConfigurations ? () => setOpen(true) : undefined}
    >
      <Dropdown
        dropdownContent={<DropdownContent />}
        open={open && canOpenMoreConfigurations}
        onOpenChange={setOpen}
        offset={5.5}
        disabled={!canOpenMoreConfigurations}
        popupClassName={popupClass}
        placement="bottom"
      >
        <Icon iconUrl={PlusIcon} />
      </Dropdown>
    </HoverTooltip.div>
  )
}
